import { useState, useEffect, useCallback } from 'react';
import { Spinner } from '../../components/Spinner';
import type { FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// ── Painpoint drill-down — supporting evidence + cluster confidence + validation

// URLs come from scraped, untrusted markdown. Only render http(s) as a clickable
// link — React does not sanitize href schemes, so a `javascript:` URL would
// otherwise execute on click.
function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

interface Painpoint {
  id: number;
  run_id: number;
  statement: string;
  frequency: number;
  source_diversity: number;
  intent_score: number;
  sentiment: number;
  engagement_norm: number;
  composite_score: number;
  low_confidence: number;
  extracted_by: string;
}
interface Evidence {
  item_id: number;
  platform: string;
  url: string | null;
  author: string | null;
  container: string | null;
  text_excerpt: string;
  raw_engagement: number | null;
  engagement_metrics: Record<string, number>;
  captured_at: string | null;
  percentile: number | null;
  engagement_rate: number | null;
  cluster_id: number | null;
  cluster_summary: string | null;
  uncertainty_flag: string | null;
  engine_score: number | null;
  cluster_sources: string[];
}
interface Validation {
  amazon_review_signal: string | null;
  search_volume_signal: string | null;
  verdict: string | null;
  notes: string | null;
}
interface Detail {
  painpoint: Painpoint;
  evidence: Evidence[];
  validation: Validation | null;
}

const PLATFORM_EMOJI: Record<string, string> = {
  reddit: '🟠', x: '🔵', hackernews: '🟧', web: '🌐', digg: '⛏️',
  github: '🐙', youtube: '▶️', polymarket: '🎲', amazon: '📦',
};

export default function PainpointDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<Validation>({
    amazon_review_signal: '', search_volume_signal: '', verdict: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/painpoints/painpoints/${id}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: Detail = await r.json();
      setData(d);
      if (d.validation) {
        setForm({
          amazon_review_signal: d.validation.amazon_review_signal || '',
          search_volume_signal: d.validation.search_volume_signal || '',
          verdict: d.validation.verdict || '',
          notes: d.validation.notes || '',
        });
      }
    } catch (e) { setErr((e as Error).message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function saveValidation(e: FormEvent) {
    e.preventDefault();
    setSaving(true); setSaved(false);
    try {
      const r = await fetch(`/api/painpoints/painpoints/${id}/validation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSaved(true);
    } catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  if (err) return <div className="max-w-4xl mx-auto bg-red-50 text-red-700 rounded-lg p-4 text-sm">{err}</div>;
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-16">
        <Spinner size={32} />
      </div>
    );
  }

  const { painpoint: p, evidence } = data;

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-800 mb-4">
        ← Back
      </button>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-bold text-gray-900">{p.statement}</h2>
          {p.low_confidence ? (
            <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">⚠ low confidence</span>
          ) : (
            <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">solid evidence</span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-5">
          {[
            ['Composite', p.composite_score.toFixed(1)],
            ['Frequency', String(p.frequency)],
            ['Intent', p.intent_score.toFixed(2)],
            ['Sentiment', p.sentiment.toFixed(2)],
            ['Engagement', `${(p.engagement_norm * 100).toFixed(0)}%`],
            ['Sources', String(p.source_diversity)],
          ].map(([label, val]) => (
            <div key={label}>
              <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
              <div className="text-lg font-semibold text-gray-900">{val}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-400 mt-4">
          Extracted by {p.extracted_by} · from run #{p.run_id}
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Supporting evidence ({evidence.length})
      </h3>
      <div className="space-y-3 mb-8">
        {evidence.map((e) => (
          <div key={e.item_id} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <span>{PLATFORM_EMOJI[e.platform] || '•'}</span>
                <span className="font-medium text-gray-700">{e.platform}</span>
                {e.author && <span className="text-gray-400">· {e.author}</span>}
                {e.container && <span className="text-gray-400">· {e.container}</span>}
                {e.captured_at && <span className="text-gray-400">· {e.captured_at}</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {e.raw_engagement != null && (
                  <span>
                    {e.platform === 'amazon' ? 'helpful votes' : 'raw'}: {e.raw_engagement}
                  </span>
                )}
                {e.percentile != null && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100">
                    {(e.percentile * 100).toFixed(0)}th pct (in {e.platform})
                  </span>
                )}
                {e.engagement_rate != null && <span>rate: {e.engagement_rate.toFixed(3)}</span>}
              </div>
            </div>
            <p className="text-sm text-gray-800 mt-2">{e.text_excerpt}</p>
            {e.url && (
              isHttpUrl(e.url) ? (
                <a href={e.url} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-blue-600 hover:underline break-all mt-1 inline-block">
                  {e.url}
                </a>
              ) : (
                <span className="text-xs text-gray-400 break-all mt-1 inline-block">{e.url}</span>
              )
            )}
            {(e.cluster_summary || e.uncertainty_flag) && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                <span className="font-medium text-gray-600">Engine cluster</span>
                {e.engine_score != null && <span> · score {e.engine_score}</span>}
                {e.cluster_sources?.length > 0 && <span> · {e.cluster_sources.join(', ')}</span>}
                {e.uncertainty_flag && (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{e.uncertainty_flag}</span>
                )}
                {e.cluster_summary && <div className="mt-1 italic">{e.cluster_summary}</div>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Validation gate — v1 manual; hooks for Amazon reviews + search volume later */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Validation gate</h3>
        <p className="text-xs text-gray-400 mb-4">
          Manual in v1. The Amazon 1–3★ review signal and search-volume fields are placeholders for
          the automated validation step planned for a later version.
        </p>
        <form onSubmit={saveValidation} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            value={form.amazon_review_signal || ''} placeholder="Amazon 1–3★ review signal"
            onChange={(e) => setForm({ ...form, amazon_review_signal: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <input
            value={form.search_volume_signal || ''} placeholder="Search-volume signal"
            onChange={(e) => setForm({ ...form, search_volume_signal: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <select
            value={form.verdict || ''}
            onChange={(e) => setForm({ ...form, verdict: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Verdict…</option>
            <option value="validated">Validated</option>
            <option value="rejected">Rejected</option>
            <option value="needs_more_data">Needs more data</option>
          </select>
          <input
            value={form.notes || ''} placeholder="Notes"
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={saving}
              className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium disabled:opacity-40">
              {saving ? 'Saving…' : 'Save validation'}
            </button>
            {saved && <span className="text-sm text-green-600">Saved ✓</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
